import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Loader2, AlertCircle } from 'lucide-react';
import { stripeService } from '../lib/stripe';
import { getProductByPriceId } from '../stripe-config';
import { useAuth } from '../context/AuthContext';

const SubscriptionStatus: React.FC = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<{ status?: string; cancel_at_period_end?: boolean } | any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


   const getSubscriptionStatus = (sub: { status?: string; cancel_at_period_end?: boolean }) => {
    if (!sub) return 'inactive';
    if (sub.status === 'canceled') return 'inactive';
    if (sub.cancel_at_period_end) return 'canceling';
    return sub.status || 'inactive';
  };

   const status = getSubscriptionStatus(subscription);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setError(null); // Clear any previous errors
        const subscriptionData = await stripeService.getUserSubscription(user.id);
        setSubscription(subscriptionData);
      } catch (err: any) {
        console.error('Error fetching subscription:', err);
        // Only set error for actual failures, not for missing data
        if (err.code !== 'PGRST116') {
          setError('Failed to load subscription status');
        } else {
          // For PGRST116 errors, log but don't show error to user
          console.warn('Multiple subscription records found, this should be investigated');
          setSubscription(null); // Fallback to basic plan
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  // Additional safety check for subscription data
  const safeSubscription = subscription && typeof subscription === 'object' ? subscription : null;

  if (!user || loading) {
    return (
      <div className="flex items-center space-x-2 text-slate-gray">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center space-x-2 text-red-500">
        <AlertCircle className="w-4 h-4" />
        <span className="text-sm">Error loading plan</span>
      </div>
    );
  }

  const getProductInfo = () => {
    if (!safeSubscription?.price_id) return null;
    return getProductByPriceId(safeSubscription.price_id);
  };

  const product = getProductInfo();
  const isActive = safeSubscription?.status === 'active';
  const isPremium = product && isActive;

  // return (
  //   <motion.div
  //     initial={{ opacity: 0, scale: 0.9 }}
  //     animate={{ opacity: 1, scale: 1 }}
  //     className="flex items-center space-x-2"
  //   >
  //     {isPremium ? (
  //       <>
  //         <div className="w-6 h-6 bg-gradient-primary rounded-full flex items-center justify-center">
  //           <Crown className="w-3 h-3 text-white" />
  //         </div>
  //         <span className="text-sm font-medium text-electric-blue">
  //           {product.name}
  //         </span>
  //       </>
  //     ) : (
  //       <>
  //         <div className="w-6 h-6 bg-slate-gray/20 rounded-full flex items-center justify-center">
  //           <Crown className="w-3 h-3 text-slate-gray" />
  //         </div>
  //         <span className="text-sm font-medium text-slate-gray">
  //           Basic Plan
  //         </span>
  //       </>
  //     )}
  //   </motion.div>
  // );

   return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex items-center space-x-2"
    >
      {status === 'active' ? (
        <>
          <div className="w-6 h-6 bg-gradient-primary rounded-full flex items-center justify-center">
            <Crown className="w-3 h-3 text-white" />
          </div>
          <span className="text-sm font-medium text-electric-blue whitespace-nowrap">
            {product?.name || 'Premium Plan'}
          </span>
        </>
      ) : status === 'canceling' ? (
        <>
          <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
            <Crown className="w-3 h-3 text-yellow-600" />
          </div>
          <span className="text-sm font-medium text-yellow-600 whitespace-nowrap">
            Canceling Soon
          </span>
        </>
      ) : (
        <>
          <div className="w-6 h-6 bg-slate-gray/20 rounded-full flex items-center justify-center">
            <Crown className="w-3 h-3 text-slate-gray" />
          </div>
          <span className="text-sm font-medium text-slate-gray whitespace-nowrap">
            Basic Plan
          </span>
        </>
      )}
    </motion.div>
  );
};

export default SubscriptionStatus;